import type { Address, WiringInstructions } from '@Types/WiringInstrucions.interface';

const ADDRESS_FIELDS = ['address1', 'address2', 'city', 'state', 'postalCode'] as const;

/**
 * True when the address carries at least one field with something other than
 * whitespace. Wiring instructions always ship a fully-formed address object, so
 * "absent" has to be detected by content rather than by null.
 */
export const hasAddressContent = (address: Address | null | undefined): boolean =>
    ADDRESS_FIELDS.some(field => !!address?.[field]?.trim());

/**
 * The address to display for the credit-to account holder on a Confirm request.
 *
 * `wiringInstructions.personalAddress` IS the Confirm address field. The agent
 * enters it in the portal, the participant corrects it in this flow, and both
 * writes land in the same place — it is also what the request-bff Confirm mapper
 * and the completion write in certifid-v2-requests read and write. Keep saving a
 * Confirm address there.
 *
 * `creditTo.accountAddress` is the bank-sourced slot (Send / ISO 20022). On
 * Confirm it is read only as a prefill fallback, so a request created before an
 * address was captured can still show the bank's copy instead of nothing. Never
 * write a Confirm address into it — the client completion write is a nested Set
 * on `personalAddress` specifically so a participant-authenticated call cannot
 * reach `CreditTo` or `BeneficiaryBank`.
 */
export const resolveCreditToPersonalAddress = (
    wi: WiringInstructions | null | undefined,
): Address | null => {
    if (hasAddressContent(wi?.personalAddress)) return wi!.personalAddress;
    if (hasAddressContent(wi?.creditTo?.accountAddress)) return wi!.creditTo.accountAddress;
    return null;
};
